/**
 * Browser-side image preprocessing: EXIF auto-rotate + JPEG re-encode.
 *
 * Why:
 *   - iPhones store portrait photos as landscape with an EXIF orientation
 *     tag. Some CDNs / <img> renderers ignore the tag and the photo shows
 *     up sideways. Re-encoding through a canvas bakes the correct
 *     orientation in.
 *   - Modern phones produce 4-8 MB photos. After our typical resize
 *     ceiling (2048px max edge) and JPEG quality 0.85, the file ends up
 *     1-1.5 MB with no visible loss. Saves R2 cost ~75 % and uploads on
 *     a weak signal feel instant.
 *
 * Videos are not re-encoded — true lossless transcoding in the browser
 * needs ffmpeg.wasm (~25 MB download + slow). Modern phones already use
 * efficient h.264/HEVC encoding so we ship the file as-is.
 */

const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.86;

function readEXIFOrientation(arrayBuffer) {
  // Bare-bones EXIF orientation parser — sufficient for iPhone / Android
  // JPEGs. Returns 1..8 (1 = upright, default).
  const view = new DataView(arrayBuffer);
  if (view.getUint16(0, false) !== 0xffd8) return 1; // not a JPEG
  const length = view.byteLength;
  let offset = 2;
  while (offset < length) {
    if (offset + 2 > length) return 1;
    const marker = view.getUint16(offset, false);
    offset += 2;
    if (marker === 0xffe1) {
      if (view.getUint32(offset + 2, false) !== 0x45786966) return 1; // not EXIF
      const little = view.getUint16(offset + 8, false) === 0x4949;
      const tiffOffset = offset + 8;
      const dirStart = tiffOffset + view.getUint32(offset + 12, little);
      const tags = view.getUint16(dirStart, little);
      for (let i = 0; i < tags; i++) {
        const tagOffset = dirStart + 2 + i * 12;
        if (view.getUint16(tagOffset, little) === 0x0112) {
          return view.getUint16(tagOffset + 8, little);
        }
      }
      return 1;
    }
    if ((marker & 0xff00) !== 0xff00) break;
    offset += view.getUint16(offset, false);
  }
  return 1;
}

/** Rotate / mirror a canvas context based on EXIF orientation 1..8. */
function applyOrientation(ctx, orientation, w, h) {
  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, w, 0);
      break;
    case 3:
      ctx.transform(-1, 0, 0, -1, w, h);
      break;
    case 4:
      ctx.transform(1, 0, 0, -1, 0, h);
      break;
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx.transform(0, 1, -1, 0, h, 0);
      break;
    case 7:
      ctx.transform(0, -1, -1, 0, h, w);
      break;
    case 8:
      ctx.transform(0, -1, 1, 0, 0, w);
      break;
    default:
      // 1 = no transform.
      break;
  }
}

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

function readArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsArrayBuffer(blob);
  });
}

/**
 * Compress a single image File through canvas. Returns a new File with
 * the original name (extension swapped to .jpg) or the original File if
 * compression isn't beneficial.
 *
 * For non-image files, returns the input unchanged.
 */
export async function compressImageFile(file) {
  if (!file || !file.type?.startsWith("image/")) return file;
  // PNG with transparency / GIF — skip to avoid breaking the alpha channel.
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;

  let orientation = 1;
  if (file.type === "image/jpeg" || file.type === "image/jpg") {
    try {
      const buf = await readArrayBuffer(file);
      orientation = readEXIFOrientation(buf);
    } catch (_) {
      orientation = 1;
    }
  }

  let img;
  try {
    img = await loadImage(file);
  } catch (_) {
    return file; // unreadable — fall back
  }

  const swap = orientation >= 5 && orientation <= 8;
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const longEdge = Math.max(srcW, srcH);
  const scale = longEdge > MAX_EDGE ? MAX_EDGE / longEdge : 1;
  const targetW = Math.round(srcW * scale);
  const targetH = Math.round(srcH * scale);

  const canvas = document.createElement("canvas");
  canvas.width = swap ? targetH : targetW;
  canvas.height = swap ? targetW : targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.imageSmoothingQuality = "high";
  applyOrientation(ctx, orientation, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const blob = await new Promise((res) =>
    canvas.toBlob(res, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) return file;

  // Don't replace if compression somehow made it bigger (e.g. tiny photo
  // already optimised) — return the original.
  if (blob.size >= file.size && scale === 1 && orientation === 1) {
    return file;
  }
  const newName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
}

/**
 * Process a batch of files — compress images, pass everything else
 * through. Used by the upload pipeline so the user just hands us files
 * and we ship the most efficient versions to R2.
 */
export async function preprocessFiles(files) {
  const arr = Array.from(files || []);
  return Promise.all(
    arr.map((f) => compressImageFile(f).catch(() => f)),
  );
}
